import { ArrowLeft, Plus } from "lucide-react";
import React from "react";

import {
  Button,
  PermissionGuard,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { toast } from "@/components/ui/use-toast";
import { getPanelBrandSlug } from "@/config/config";
import { useI18n } from "@/context/i18n";

import { TableProducts } from "./components";
import { CreditProductForm } from "./components/products-form/credit-product-form";
import { InsuranceAutoForm } from "./components/products-form/insurance-auto-form";
import { InsuranceFuneralForm } from "./components/products-form/insurance-funeral-form";
import { InsuranceGroupLifeForm } from "./components/products-form/insurance-group-life-form";
import { InsurancePersonalAccidentForm } from "./components/products-form/insurance-personal-accident-form";
import { InsuranceWarrantyForm } from "./components/products-form/insurance-warranty-form";
import { isInfinityAssistanceProduct } from "./products.assistance-variant";
import { useProduct } from "./products.hooks";
import { EProductType } from "./products.type";
import { getProductCatalogEntriesForUi } from "./products-catalog";
import { useAllowedProductTypes } from "./use-allowed-product-types";

export const ProductsPage: React.FC = () => {
  const { t } = useI18n();
  const { allowedTypes } = useAllowedProductTypes();
  const panelBrand = getPanelBrandSlug();

  const catalogEntries = React.useMemo(
    () => getProductCatalogEntriesForUi(allowedTypes, panelBrand),
    [allowedTypes, panelBrand]
  );

  const {
    isProductFormOpen,
    setIsProductFormOpen,
    productForm,
    openProductForm,
    createProduct,
    updateProduct,
    deleteProduct,
    products,
    productType,
    setProductType,
    editingProductId,
    editingProduct,
    isEditing,
  } = useProduct();

  return (
    <div className="flex min-h-0 w-full flex-col space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            {isProductFormOpen && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsProductFormOpen(false);
                  setProductType(null);
                }}
                size="sm"
                className="p-3 hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {productType
                ? productType === EProductType.INSURANCE_ASSISTANCE &&
                  panelBrand === "soulhub"
                  ? "Assistência - Soulhub"
                  : t(`product.type.${productType}`)
                : t("products.title")}
            </h1>
          </div>
          {!productType && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("products.description")}
            </p>
          )}
        </div>
        {!isProductFormOpen && (
          <div className="flex justify-end">
            <PermissionGuard permission="products:create">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    className="shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("products.button.new.product")}
                  </Button>
                </PopoverTrigger>
              <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0">
                <div className="grid max-h-[min(70vh,32rem)] gap-0 overflow-hidden">
                  <div className="space-y-2 border-b px-4 py-3">
                    <h4 className="leading-none font-medium">
                      {t("products.catalog.popover.title")}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {t("products.catalog.popover.description")}
                    </p>
                  </div>
                  <div className="overflow-y-auto px-2 py-2">
                    <Select
                      onValueChange={(value) => {
                        const entry = catalogEntries.find(
                          (e) => e.id === value
                        );
                        if (!entry) {
                          return;
                        }
                        if (!entry.implemented) {
                          toast({
                            title: t("products.catalog.coming_soon.title"),
                            description: `${t(entry.labelKey)} — ${t(
                              "products.catalog.coming_soon.description"
                            )}`,
                          });
                          return;
                        }
                        setProductType(entry.productType!);
                        openProductForm();
                      }}
                    >
                      <SelectTrigger className="w-full min-w-0">
                        <SelectValue
                          placeholder={t(
                            "products.form.fields.product_type.placeholder"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent className="max-h-[min(60vh,24rem)]">
                        <SelectGroup>
                          {catalogEntries.map((entry) => (
                            <SelectItem key={entry.id} value={entry.id}>
                              {entry.id === EProductType.INSURANCE_ASSISTANCE &&
                              panelBrand === "soulhub"
                                ? "Assistência - Soulhub"
                                : t(entry.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            </PermissionGuard>
          </div>
        )}
      </div>
      <InsurancePersonalAccidentForm
        open={isProductFormOpen && productType === EProductType.INSURANCE_AP}
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      <InsuranceGroupLifeForm
        open={isProductFormOpen && productType === EProductType.INSURANCE_VG}
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      {panelBrand === "soulhub" &&
      !(isEditing && isInfinityAssistanceProduct(editingProduct)) ? (
        <InsuranceWarrantyForm
          open={
            isProductFormOpen &&
            productType === EProductType.INSURANCE_ASSISTANCE
          }
          onOpenChange={() => {}}
          form={productForm}
          isEditMode={isEditing}
          assistanceSoulhubMode
          productKind="warranty"
          onSubmit={(data) => {
            if (isEditing && editingProductId) {
              updateProduct(editingProductId, data);
            } else {
              createProduct(data);
            }
          }}
        />
      ) : (
        <InsuranceFuneralForm
          open={
            isProductFormOpen &&
            productType === EProductType.INSURANCE_ASSISTANCE
          }
          onOpenChange={() => {}}
          form={productForm}
          isEditMode={isEditing}
          onSubmit={(data) => {
            if (isEditing && editingProductId) {
              updateProduct(editingProductId, data);
            } else {
              createProduct(data);
            }
          }}
        />
      )}
      <InsuranceAutoForm
        key={`${editingProductId || "new"}-${productType || "vehicle"}`}
        open={
          isProductFormOpen &&
          (productType === EProductType.AUTO ||
            productType === EProductType.MOTO)
        }
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      <InsuranceWarrantyForm
        open={
          isProductFormOpen &&
          (productType === EProductType.WARRANTY_INSURANCE ||
            productType === EProductType.THEFT_INSURANCE)
        }
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        productKind={
          productType === EProductType.THEFT_INSURANCE ? "theft" : "warranty"
        }
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      <CreditProductForm
        open={isProductFormOpen && productType === EProductType.CREDIT}
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      {!isProductFormOpen && (
        <TableProducts products={products} deleteProduct={deleteProduct} />
      )}
    </div>
  );
};
